/**
=========================================================
* Elite 247 Admin Panel - Clarity Call Bookings
=========================================================
*/

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import RecordTable from "components/RecordTable";
import CallButton from "components/CallButton";
import ContactMessageDialog from "components/ContactMessageDialog";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { adminApi } from "services/api";
import { courseLabel, COURSE_OPTIONS } from "utils/courses";
import { clarityCallBookingsToRows } from "utils/exportUtils";
import { BOOKING_STATUSES } from "utils/bookingStatuses";

const STATUS_FILTER_OPTIONS = BOOKING_STATUSES.map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase().replace("_", " "),
}));
const DATE_FIELDS = [
  { value: "created", label: "Booked on" },
  { value: "call", label: "Call date" },
];
const EXPORT_CONFIG = {
  fetchAll: adminApi.exportClarityCallBookings,
  toRows: clarityCallBookingsToRows,
  filename: "clarity-call-bookings",
  title: "Clarity Call Bookings",
};
const WAT = "Africa/Lagos";

const STATUS_COLORS = {
  REQUESTED: "secondary",
  PENDING: "warning",
  CONFIRMED: "info",
  COMPLETED: "success",
  CANCELLED: "error",
  NO_SHOW: "secondary",
};

function formatIn(value, timeZone) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-GB", {
      timeZone,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return new Date(value).toLocaleString("en-GB");
  }
}

// Below md the table becomes stacked cards: give touch targets ~40px there, keeping desktop sizes.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };
const SMALL_BUTTON_TAP_SX = { minHeight: { xs: "2.5rem", md: "2rem" } };

function ClarityCallBookings() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [messageTarget, setMessageTarget] = useState(null);
  // Bumped on every open and close: a detail response is applied only if its request is still
  // the current one, so a late reply can't fill a closed dialog or one showing another booking.
  const detailRequest = useRef(0);

  const openDetails = async (booking) => {
    detailRequest.current += 1;
    const requestId = detailRequest.current;
    setDetail(booking);
    setCopied(false);
    setCopyError("");
    setDetailLoading(true);
    try {
      const full = await adminApi.getClarityCallBooking(booking._id);
      if (detailRequest.current === requestId) setDetail(full);
    } catch (err) {
      // The dialog keeps the list row's data; the manage and Teams links need the full record.
      console.error(`Failed to load clarity call booking ${booking._id}:`, err);
    } finally {
      if (detailRequest.current === requestId) setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    detailRequest.current += 1;
    setDetail(null);
    setDetailLoading(false);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    setError("");
    setSuccess("");
    setWarning("");
    try {
      const response = await adminApi.updateClarityCallBookingStatus(bookingId, newStatus);
      // Cancelled, but Teams refused to remove the meeting: it is still on the calendar.
      if (response?.teamsError) setWarning(response.message);
      else setSuccess("Status updated");
      setReloadToken((t) => t + 1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const copyManageLink = async () => {
    try {
      await navigator.clipboard.writeText(detail.manageUrl);
      setCopyError("");
      setCopied(true);
    } catch {
      setCopyError("Couldn't copy — select the link above and copy it manually.");
    }
  };

  const columns = [
    { Header: "name", accessor: "name", width: "14%", align: "left" },
    { Header: "email", accessor: "email", width: "16%", align: "left" },
    { Header: "phone", accessor: "phone", width: "12%", align: "left" },
    { Header: "course", accessor: "course", width: "12%", align: "left" },
    { Header: "call time", accessor: "callTime", width: "16%", align: "left" },
    { Header: "status", accessor: "status", width: "14%", align: "center" },
    { Header: "booked on", accessor: "bookedOn", width: "9%", align: "left" },
    { Header: "actions", accessor: "actions", width: "7%", align: "center" },
  ];

  const renderRow = (booking) => ({
    name: (
      <MDTypography variant="button" fontWeight="medium">
        {booking.name}
      </MDTypography>
    ),
    email: <MDTypography variant="caption">{booking.email}</MDTypography>,
    phone: (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption">{booking.phone || "—"}</MDTypography>
        <CallButton phoneNumber={booking.phone} />
      </MDBox>
    ),
    course: booking.courseSlug ? (
      <Chip
        label={courseLabel(booking.courseSlug)}
        size="small"
        color="secondary"
        variant="outlined"
      />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    callTime: booking.startDateTime ? (
      <MDBox>
        <MDTypography variant="caption" display="block">
          {formatIn(booking.startDateTime, WAT)} WAT
        </MDTypography>
        {booking.visitorTimeZone && booking.visitorTimeZone !== WAT && (
          <MDTypography variant="caption" color="text" display="block">
            {formatIn(booking.startDateTime, booking.visitorTimeZone)} · {booking.visitorTimeZone}
          </MDTypography>
        )}
      </MDBox>
    ) : (
      <MDTypography variant="caption" color="text">
        To be scheduled
      </MDTypography>
    ),
    status: (
      <FormControl size="small" fullWidth>
        <Select
          value={booking.status}
          onChange={(e) => handleStatusChange(booking._id, e.target.value)}
          sx={{ minWidth: 130 }}
        >
          {BOOKING_STATUSES.map((option) => (
            <MenuItem key={option} value={option}>
              <Chip label={option} color={STATUS_COLORS[option]} size="small" />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
    bookedOn: (
      <MDTypography variant="caption">
        {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : "—"}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" justifyContent="center" gap={0.5}>
        <Tooltip title="View details">
          <IconButton onClick={() => openDetails(booking)} size="small" sx={ROW_ACTION_SX}>
            <Icon>visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Send message">
          <IconButton
            onClick={() => setMessageTarget(booking)}
            size="small"
            color="primary"
            sx={ROW_ACTION_SX}
          >
            <Icon>send</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  const field = (label, value) =>
    value || value === 0 ? (
      <MDBox mb={1.5} key={label}>
        <MDTypography variant="button" fontWeight="bold">
          {label}:
        </MDTypography>{" "}
        <MDTypography variant="body2" component="span">
          {value}
        </MDTypography>
      </MDBox>
    ) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={{ xs: 2, md: 3 }}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  <Icon sx={{ color: "white", fontSize: 28 }}>calendar_today</Icon>
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      Clarity Call Bookings
                    </MDTypography>
                    <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                      Booked and requested Career Clarity Calls from every course
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDButton
                  component={Link}
                  to="/clarity-call-bookings/availability"
                  variant="outlined"
                  color="white"
                  size="small"
                  startIcon={<Icon>schedule</Icon>}
                  sx={SMALL_BUTTON_TAP_SX}
                >
                  Availability
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}
              {warning && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="warning" dismissible onClose={() => setWarning("")}>
                    {warning}
                  </MDAlert>
                </MDBox>
              )}
              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getClarityCallBookings}
                renderRow={renderRow}
                statusOptions={STATUS_FILTER_OPTIONS}
                courseOptions={COURSE_OPTIONS}
                dateFields={DATE_FIELDS}
                reloadToken={reloadToken}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No clarity call bookings yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(detail)}
        onClose={closeDetails}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle>
          Clarity Call Booking
          {detail && (
            <Chip
              label={detail.status}
              color={STATUS_COLORS[detail.status]}
              size="small"
              sx={{ ml: 2, verticalAlign: "middle" }}
            />
          )}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {detailLoading && (
            <MDTypography variant="caption" display="block" mb={1}>
              Loading…
            </MDTypography>
          )}
          {detail && (
            <>
              {[
                field("Name", detail.name),
                field("Email", detail.email),
                field(
                  "Phone",
                  detail.phone ? (
                    <MDBox component="span" display="inline-flex" alignItems="center" gap={0.5}>
                      {detail.phone}
                      <CallButton phoneNumber={detail.phone} />
                    </MDBox>
                  ) : null
                ),
                field("Country", detail.country),
                field("Course", courseLabel(detail.courseSlug)),
                field("Level", detail.level),
                field("Goal", detail.goal),
                field(
                  "Call time (WAT)",
                  detail.startDateTime
                    ? `${formatIn(detail.startDateTime, WAT)} – ${formatIn(
                        detail.endDateTime,
                        WAT
                      )}`
                    : "To be scheduled"
                ),
                field(
                  "Visitor's time",
                  detail.startDateTime && detail.visitorTimeZone
                    ? `${formatIn(detail.startDateTime, detail.visitorTimeZone)} (${
                        detail.visitorTimeZone
                      })`
                    : null
                ),
                field(
                  "Rescheduled",
                  detail.rescheduleCount ? `${detail.rescheduleCount} time(s)` : null
                ),
                field(
                  "Cancelled",
                  detail.cancelledAt
                    ? `${new Date(detail.cancelledAt).toLocaleString()} by ${
                        detail.cancelledBy || "—"
                      }`
                    : null
                ),
                field("Cancellation reason", detail.cancellationReason),
                field(
                  "Booked on",
                  detail.createdAt ? new Date(detail.createdAt).toLocaleString() : null
                ),
              ]}
              {detail.joinUrl &&
                field(
                  "Teams link",
                  <a href={detail.joinUrl} target="_blank" rel="noreferrer">
                    {detail.joinUrl}
                  </a>
                )}
              {detail.manageUrl && (
                <MDBox
                  mt={2}
                  p={1.5}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                >
                  <MDTypography variant="button" fontWeight="bold" display="block">
                    Visitor&apos;s manage link
                  </MDTypography>
                  <MDTypography variant="caption" display="block" sx={{ wordBreak: "break-all" }}>
                    {detail.manageUrl}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    {detail.status === "REQUESTED"
                      ? "Send this on WhatsApp so they can pick their own time — it books the Teams meeting for you."
                      : "Lets the visitor reschedule or cancel. Treat it like a password."}
                  </MDTypography>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={copyManageLink}
                    startIcon={<Icon>content_copy</Icon>}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </MDButton>
                  {copyError && (
                    <MDTypography variant="caption" color="error" display="block" mt={1}>
                      {copyError}
                    </MDTypography>
                  )}
                </MDBox>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {detail && (
            <MDButton
              color="primary"
              startIcon={<Icon>send</Icon>}
              onClick={() => {
                const target = detail;
                closeDetails();
                setMessageTarget(target);
              }}
            >
              Send message
            </MDButton>
          )}
          <MDButton onClick={closeDetails} color="info">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {messageTarget && (
        <ContactMessageDialog
          key={messageTarget._id}
          open
          onClose={() => setMessageTarget(null)}
          contact={messageTarget}
          send={(payload) => adminApi.sendMessageToClarityCallBooker(messageTarget._id, payload)}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default ClarityCallBookings;
